import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import { SalutationOptionType, Salutations } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { startCase } from 'lodash';
import { Observable } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import {
	EditingSalutationsReLinesDialogComponent,
	ReLineType,
} from '../editing-dialog/editing-salutations-relines-dialog.component';
import { TestHooks } from './file-salutations.hooks';

export const textareaEditorStates = [
	{ value: false, label: 'View' },
	{ value: true, label: 'Edit' },
];

@UntilDestroy()
@Component({
	selector: 'app-file-salutations',
	templateUrl: './file-salutations.component.html',
	styleUrls: ['./file-salutations.component.scss'],
	standalone: false,
})
export class FileSalutationsComponent implements OnInit {
	testHooks = TestHooks;
	public salutationTypes = SalutationOptionType;
	public salutationTypesDropdownOptions = Object.values(
		SalutationOptionType,
	).map((val) => ({ name: startCase(val), value: val }));
	public salutations: Salutations;
	public editing = false;
	public reLineTypes = [
		{ label: 'General', value: 'salutations.reLineGeneral' },
		{ label: 'Condensed', value: 'salutations.reLineCondensed' },
		{ label: 'Client', value: 'salutations.reLineClients' },
	];

	public selectedReLineType: ReLineType = 'salutations.reLineGeneral';
	readonly ICON = ICON;

	public states = textareaEditorStates;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(MatterClients.get_matterSalutations)
	salutations$: Observable<Salutations>;
	@Select(TagsSelector.getTagInjectedDocument) parsedDocument$: Observable<
		(doc: string) => string
	>;

	public get primaryType() {
		return startCase(this.salutations.primaryType);
	}

	public get envelopeType() {
		return startCase(this.salutations.envelopeType);
	}

	public envelopeSalutationChanged = (event: string): void => {
		this.matterDispatch.SetSalutationsEnvelope(this.matterId, event);
	};

	public envelopeSalutationAdditionalChanged = (event: string): void => {
		this.matterDispatch.SetSalutationsEnvelopeAdditional(this.matterId, event);
	};

	public envelopeSalutationTypeChanged = (
		event: DropdownResult<DropdownOption<SalutationOptionType>>,
	): void => {
		this.matterDispatch.SetSalutationsEnvelopeType(
			this.matterId,
			event.result.value,
		);
	};

	public primarySalutationChanged = (event: string): void => {
		this.matterDispatch.SetSalutationsPrimary(this.matterId, event);
	};

	public primarySalutationTypeChanged = (
		event: DropdownResult<DropdownOption<SalutationOptionType>>,
	): void => {
		this.matterDispatch.SetSalutationsPrimaryType(
			this.matterId,
			event.result.value,
		);
	};

	constructor(
		private matterDispatch: MatterDispatch,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.salutations$.pipe(untilDestroyed(this)).subscribe((salutations) => {
			this.salutations = { ...salutations };
		});
	}

	changeEditingStatusAndSave(): void {
		if (this.editing) {
			if (this.selectedReLineType === 'salutations.reLineGeneral') {
				this.matterDispatch.SetSalutationsReLineGeneral(
					this.matterId,
					this.salutations.reLineGeneral,
				);
			} else if (this.selectedReLineType === 'salutations.reLineCondensed') {
				this.matterDispatch.SetSalutationsReLineCondensed(
					this.matterId,
					this.salutations.reLineCondensed,
				);
			} else if (this.selectedReLineType === 'salutations.reLineClients') {
				this.matterDispatch.SetSalutationsReLineClients(
					this.matterId,
					this.salutations.reLineClients,
				);
			}
		}
		this.editing = !this.editing;
	}

	onChange($event: string): void {
		if (this.selectedReLineType === 'salutations.reLineGeneral') {
			this.salutations.reLineGeneral = $event;
		} else if (this.selectedReLineType === 'salutations.reLineCondensed') {
			this.salutations.reLineCondensed = $event;
		} else if (this.selectedReLineType === 'salutations.reLineClients') {
			this.salutations.reLineClients = $event;
		}
	}

	editText(
		matterId: string,
		type: ReLineType,
		tagValue: string,
		tagLabel: string,
	): void {
		this.dialog.open(EditingSalutationsReLinesDialogComponent, {
			data: {
				matterId,
				type,
				tagValue,
				tagLabel,
			},
			maxHeight: '90vh',
			maxWidth: '90vw',
			width: '40vw',
		});
	}

	getReline(reLineType: ReLineType) {
		switch (reLineType) {
			case 'salutations.reLineGeneral':
				return this.salutations.reLineGeneral;
			case 'salutations.reLineCondensed':
				return this.salutations.reLineCondensed;
			case 'salutations.reLineClients':
				return this.salutations.reLineClients;
		}
	}

	setRelineType($event: ReLineType): void {
		this.selectedReLineType = $event;
	}

	reset(): void {
		this.matterDispatch.ResetSaluationsAndRelines(this.matterId);
	}
}
